<!-- app-subdomain-creators\src\Components\NewsfeedPostPollCard.vue -->

<template>
  <div class="px-4 pb-4 pt-4">
    <div class="space-y-2">
      <div
        v-for="opt in localPoll.options"
        :key="opt.id || opt.tempKey"
        class="relative"
      >
        <button
          type="button"
          class="relative w-full overflow-hidden rounded-xl border border-zinc-300 px-3 py-3 text-left transition"
          :class="[
            optionIsSelected(opt) ? 'bg-[#eaf3ff] border-[#9cc4ff]' : 'bg-white hover:bg-zinc-50',
            readOnly ? 'cursor-default' : 'cursor-pointer'
          ]"
          :disabled="readOnly || isSubmittingVote"
          @click="handleVote(opt)"
        >
          <div
            class="absolute inset-y-0 left-0 bg-[#2D8CFF]/12 transition-all duration-300 ease-out"
            :style="{ width: `${getFillWidth(opt)}%` }"
          ></div>

          <div class="flex items-center justify-between gap-3">
            <div class="relative z-10 flex items-center gap-3 min-w-0">
              <span
                class="w-6 h-6 rounded border flex items-center justify-center transition-colors"
                :class="optionIsSelected(opt) ? 'bg-[#2D8CFF] border-[#2D8CFF] text-white' : 'border-zinc-500 text-transparent'"
              >
                <CheckIcon class="w-4 h-4" />
              </span>
              <span class="text-base text-zinc-900 truncate">{{ opt.text }}</span>
            </div>

            <div class="relative z-10 flex items-center gap-2 shrink-0">
              <button
                type="button"
                class="text-[#2D8CFF] text-base font-medium shrink-0"
                :class="canOpenVoteDetails ? 'hover:underline cursor-pointer' : 'cursor-default'"
                @click.stop="openVotesModal(opt)"
              >
                {{ getPercent(opt) }}%
              </button>

              <button
                v-if="canManage && !readOnly"
                type="button"
                class="w-7 h-7 rounded-full bg-zinc-200 hover:bg-zinc-300 text-zinc-600 flex items-center justify-center"
                aria-label="Delete option"
                @click.stop="askDeleteOption(opt)"
              >
                <XMarkIcon class="w-4 h-4" />
              </button>
            </div>
          </div>
        </button>
      </div>

      <input
        v-if="canAddOptions"
        v-model="newOptionText"
        type="text"
        class="w-full rounded-xl border border-zinc-300 px-3 py-3 text-base text-zinc-700 placeholder:text-zinc-400 focus:ring-0 focus:outline-none"
        placeholder="Add poll option..."
        @keydown.enter.prevent="handleAddOption"
      />
    </div>
  </div>

  <Transition
    enter-active-class="ease-out duration-200"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="ease-in duration-150"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="showVotesModal"
      class="fixed inset-0 bg-black/35 flex items-center justify-center z-[80]"
      @click.self="showVotesModal = false"
    >
      <div class="bg-white w-full max-w-[520px] rounded-xl border border-zinc-200 shadow-xl overflow-hidden">
        <div class="h-14 border-b border-zinc-200 flex items-center justify-center relative">
          <p class="text-xl font-semibold text-zinc-900">{{ activeVotesOption?.text || 'Votes' }}</p>
          <button
            type="button"
            class="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-zinc-200 hover:bg-zinc-300 flex items-center justify-center"
            @click="showVotesModal = false"
          >
            <XMarkIcon class="w-7 h-7 text-zinc-700" />
          </button>
        </div>

        <div class="p-4">
          <p class="text-2xl font-semibold text-zinc-900 mb-3">
            {{ getPercent(activeVotesOption) }}% · {{ getVoteCount(activeVotesOption) }} Vote{{ getVoteCount(activeVotesOption) === 1 ? '' : 's' }}
          </p>

          <div v-if="isLoadingVoters" class="text-zinc-500 text-sm">Loading voters...</div>
          <div v-else-if="!activeVoters.length" class="text-zinc-500 text-sm">No votes yet.</div>

          <div
            v-else
            ref="votersListEl"
            class="space-y-3 max-h-[360px] overflow-y-auto pr-1"
            @scroll.passive="onVotersListScroll"
          >
            <div v-for="v in visibleVoters" :key="`${v.userType || ''}:${v.userId || ''}:${v.name || ''}`" class="flex items-center gap-3">
              <img
                :src="v.avatarUrl || defaultAvatar"
                alt="Voter avatar"
                class="w-10 h-10 rounded-full object-cover"
              />
              <p class="text-base font-medium text-zinc-900">{{ v.name || 'Member' }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>

  <Transition
    enter-active-class="ease-out duration-200"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="ease-in duration-150"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 bg-black/35 flex items-center justify-center z-[81]"
      @click.self="showDeleteModal = false"
    >
      <div class="bg-white w-full max-w-[520px] rounded-xl border border-zinc-200 shadow-xl overflow-hidden">
        <div class="h-14 border-b border-zinc-200 flex items-center justify-center relative">
          <p class="text-xl font-semibold text-zinc-900">Delete option</p>
          <button
            type="button"
            class="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-zinc-200 hover:bg-zinc-300 flex items-center justify-center"
            @click="showDeleteModal = false"
          >
            <XMarkIcon class="w-7 h-7 text-zinc-700" />
          </button>
        </div>
        <div class="p-4">
          <p class="text-lg text-zinc-700">Are you sure you want to delete this option from the poll?</p>
          <div class="mt-6 flex justify-end gap-3">
            <button
              type="button"
              class="text-[#2D8CFF] text-base font-medium px-3 py-2"
              @click="showDeleteModal = false"
            >
              Cancel
            </button>
            <button
              type="button"
              class="bg-[#2D8CFF] hover:bg-[#1f7aeb] text-white text-base font-semibold px-6 py-2 rounded-lg"
              :disabled="isDeletingOption"
              @click="confirmDeleteOption"
            >
              {{ isDeletingOption ? 'Deleting...' : 'Delete' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { CheckIcon, XMarkIcon } from '@heroicons/vue/24/solid';
import { useWebsocketStore } from '@/stores/websocketStore';

const props = defineProps({
  postId: { type: [Number, String], required: true },
  post: { type: Object, default: null },
  apiProxyUrl: { type: String, required: true },
  canManage: { type: Boolean, default: false },
  readOnly: { type: Boolean, default: false },
});

const emit = defineEmits(['poll-updated']);
const websocketStore = useWebsocketStore();
let unsubscribePostPollUpdated = null;

const defaultAvatar = new URL('@/assets/media/images/default-avatar.png', import.meta.url).href;

function normalizePollFromPost(post) {
  const source = post?.poll || post?.pollData || post?.postPoll || null;
  if (!source && post?.postType !== 'poll' && post?.post_type !== 'poll') return null;

  const base = source || {};
  const rawOptions =
    base.options ||
    base.pollOptions ||
    post?.pollOptions ||
    post?.options ||
    [];

  const options = (Array.isArray(rawOptions) ? rawOptions : [])
    .map((o, idx) => {
      const text = String(o?.text || o?.optionText || o?.option_text || '').trim();
      if (!text) return null;
      return {
        id: o?.id || o?.optionId || o?.option_id || null,
        tempKey: `opt-${idx}-${text}`,
        text,
        voteCount: Number(o?.voteCount || o?.votesCount || o?.votes || 0) || 0,
        percent: Number(o?.percent || o?.votePercent || 0) || 0,
        voters: Array.isArray(o?.voters) ? o.voters : [],
        isVotedByMe: !!(o?.isVotedByMe || o?.selected || o?.isSelected),
      };
    })
    .filter(Boolean);

  const totalVotes = Number(base?.totalVotes || base?.votesCount || 0) || options.reduce((sum, o) => sum + (Number(o.voteCount) || 0), 0);
  const myVoteOptionId = base?.myVoteOptionId || base?.my_vote_option_id || options.find((o) => o.isVotedByMe)?.id || null;
  const allowAnyoneToAddOptions =
    base?.allowAnyoneToAddOptions ??
    base?.allow_anyone_to_add_options ??
    post?.allowAnyoneToAddOptions ??
    post?.allow_anyone_to_add_options ??
    false;
  const showResultsRaw =
    base?.showResults ??
    base?.show_results ??
    post?.showResults ??
    post?.show_results;
  const showResults = showResultsRaw == null ? true : !!showResultsRaw;

  return {
    id: base?.id || base?.pollId || base?.poll_id || null,
    options,
    totalVotes,
    myVoteOptionId,
    allowAnyoneToAddOptions: !!allowAnyoneToAddOptions,
    showResults,
  };
}

const localPoll = ref({
  id: null,
  options: [],
  totalVotes: 0,
  myVoteOptionId: null,
  allowAnyoneToAddOptions: false,
  showResults: true,
});

watch(
  () => props.post,
  (v) => {
    localPoll.value = normalizePollFromPost(v) || {
      id: null,
      options: [],
      totalVotes: 0,
      myVoteOptionId: null,
      allowAnyoneToAddOptions: false,
      showResults: true,
    };
  },
  { immediate: true, deep: true }
);

const currentViewerType = computed(() =>
  String(props.post?.currentUserType || props.post?.current_user_type || '').trim().toLowerCase()
);
const isCreatorViewer = computed(() => currentViewerType.value === 'creator');
const canOpenVoteDetails = computed(() => isCreatorViewer.value || !!localPoll.value?.showResults);
const canAddOptions = computed(() => {
  if (props.readOnly) return false;
  return !!localPoll.value?.allowAnyoneToAddOptions;
});

const showVotesModal = ref(false);
const activeVotesOption = ref(null);
const activeVoters = ref([]);
const votersListEl = ref(null);
const visibleVotersCount = ref(20);
const isLoadingVoters = ref(false);

const showDeleteModal = ref(false);
const pendingDeleteOption = ref(null);
const isDeletingOption = ref(false);
const isSubmittingVote = ref(false);
const newOptionText = ref('');

function optionIsSelected(opt) {
  if (!opt) return false;
  if (localPoll.value.myVoteOptionId != null && opt.id != null) {
    return String(localPoll.value.myVoteOptionId) === String(opt.id);
  }
  return !!opt.isVotedByMe;
}

function getVoteCount(opt) {
  return Number(opt?.voteCount || 0) || 0;
}

function getPercent(opt) {
  const direct = Number(opt?.percent || 0);
  if (Number.isFinite(direct) && direct > 0) return Math.round(direct);

  const total = Number(localPoll.value.totalVotes || 0);
  if (!total) return 0;
  return Math.round((getVoteCount(opt) / total) * 100);
}

function getFillWidth(opt) {
  const pct = Math.max(0, Math.min(100, getPercent(opt)));
  if (pct > 0) return pct;
  return optionIsSelected(opt) ? 8 : 0;
}

function applyPollFromBackend(poll) {
  const wrapped = { poll };
  const normalized = normalizePollFromPost(wrapped);
  if (normalized) {
    localPoll.value = normalized;
    emit('poll-updated', normalized);
  }
}

const visibleVoters = computed(() =>
  (Array.isArray(activeVoters.value) ? activeVoters.value : []).slice(0, visibleVotersCount.value)
);

function loadMoreVoters() {
  const total = Array.isArray(activeVoters.value) ? activeVoters.value.length : 0;
  if (visibleVotersCount.value >= total) return;
  visibleVotersCount.value = Math.min(total, visibleVotersCount.value + 20);
}

function onVotersListScroll() {
  const el = votersListEl.value;
  if (!el) return;
  const threshold = 48;
  const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
  if (atBottom) loadMoreVoters();
}

function normalizeVoter(v) {
  return {
    userId: v?.userId ?? v?.user_id ?? null,
    userType: v?.userType ?? v?.user_type ?? null,
    name: String(v?.name || v?.displayName || v?.display_name || 'Member'),
    avatarUrl: v?.avatarUrl || v?.avatar_url || v?.profilePic || v?.profile_pic || null,
  };
}

async function refreshPollSnapshot() {
  try {
    const resp = await fetch(
      `${props.apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(props.postId)}/poll`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );
    if (!resp.ok) return;
    const data = await resp.json().catch(() => null);
    if (data?.poll) {
      applyPollFromBackend(data.poll);
    }
  } catch (err) {
    console.error('[NewsfeedPostPollCard] refresh poll error:', err);
  }
}

function onPostPollUpdated(payload) {
  const currentPostId = Number(props.postId || 0);
  const payloadPostId = Number(payload?.postId || 0);
  if (currentPostId <= 0 || payloadPostId <= 0 || currentPostId !== payloadPostId) return;
  refreshPollSnapshot();
}

async function handleVote(opt) {
  if (props.readOnly || isSubmittingVote.value) return;
  if (!opt?.id) return;

  isSubmittingVote.value = true;
  try {
    const resp = await fetch(
      `${props.apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(props.postId)}/poll/votes`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ optionId: opt.id }),
      }
    );

    if (!resp.ok) {
      return;
    }

    const data = await resp.json().catch(() => null);
    if (data?.poll) {
      applyPollFromBackend(data.poll);
      return;
    }

    // optimistic fallback
    const previous = localPoll.value.myVoteOptionId;
    localPoll.value.myVoteOptionId = opt.id;
    localPoll.value.options = localPoll.value.options.map((o) => {
      const next = { ...o };
      if (o.id === previous && o.id !== opt.id) {
        next.voteCount = Math.max(0, getVoteCount(o) - 1);
      }
      if (o.id === opt.id && o.id !== previous) {
        next.voteCount = getVoteCount(o) + 1;
      }
      next.isVotedByMe = o.id === opt.id;
      return next;
    });

    if (previous !== opt.id) {
      if (previous == null) localPoll.value.totalVotes = Number(localPoll.value.totalVotes || 0) + 1;
    }

    emit('poll-updated', localPoll.value);
  } catch (err) {
    console.error('[NewsfeedPostPollCard] vote error:', err);
  } finally {
    isSubmittingVote.value = false;
  }
}

async function openVotesModal(opt) {
  if (!canOpenVoteDetails.value) return;
  activeVotesOption.value = opt;
  showVotesModal.value = true;
  visibleVotersCount.value = 20;
  activeVoters.value = Array.isArray(opt?.voters) ? opt.voters.map(normalizeVoter) : [];

  if (!opt?.id) return;

  isLoadingVoters.value = true;
  try {
    const resp = await fetch(
      `${props.apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(props.postId)}/poll/options/${encodeURIComponent(opt.id)}/voters`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );

    if (!resp.ok) return;
    const data = await resp.json().catch(() => null);
    const voters = Array.isArray(data?.voters) ? data.voters.map(normalizeVoter) : [];
    activeVoters.value = voters;
    visibleVotersCount.value = 20;
  } catch (err) {
    console.error('[NewsfeedPostPollCard] load voters error:', err);
  } finally {
    isLoadingVoters.value = false;
  }
}

function askDeleteOption(opt) {
  pendingDeleteOption.value = opt;
  showDeleteModal.value = true;
}

async function confirmDeleteOption() {
  if (!pendingDeleteOption.value?.id || isDeletingOption.value) return;

  isDeletingOption.value = true;
  try {
    const resp = await fetch(
      `${props.apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(props.postId)}/poll/options/${encodeURIComponent(pendingDeleteOption.value.id)}`,
      {
        method: 'DELETE',
        credentials: 'include',
      }
    );

    if (resp.ok) {
      const data = await resp.json().catch(() => null);
      if (data?.poll) {
        applyPollFromBackend(data.poll);
      } else {
        localPoll.value.options = localPoll.value.options.filter((o) => String(o.id) !== String(pendingDeleteOption.value.id));
        emit('poll-updated', localPoll.value);
      }
    }
  } catch (err) {
    console.error('[NewsfeedPostPollCard] delete option error:', err);
  } finally {
    isDeletingOption.value = false;
    showDeleteModal.value = false;
    pendingDeleteOption.value = null;
  }
}

async function handleAddOption() {
  const text = String(newOptionText.value || '').trim();
  if (!text || !canAddOptions.value) return;

  try {
    const resp = await fetch(
      `${props.apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(props.postId)}/poll/options`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text }),
      }
    );

    if (resp.ok) {
      const data = await resp.json().catch(() => null);
      if (data?.poll) {
        applyPollFromBackend(data.poll);
      } else {
        localPoll.value.options.push({
          id: `local-${Date.now()}`,
          tempKey: `local-${Date.now()}`,
          text,
          voteCount: 0,
          percent: 0,
          voters: [],
          isVotedByMe: false,
        });
      }
      newOptionText.value = '';
    }
  } catch (err) {
    console.error('[NewsfeedPostPollCard] add option error:', err);
  }
}

onMounted(() => {
  unsubscribePostPollUpdated = websocketStore.subscribe(
    'newsfeed:postPollUpdated',
    onPostPollUpdated
  );
});

onBeforeUnmount(() => {
  if (typeof unsubscribePostPollUpdated === 'function') {
    unsubscribePostPollUpdated();
    unsubscribePostPollUpdated = null;
  }
});
</script>
